import { CircularProgress, Collapse, Grid, IconButton, Typography, useTheme } from '@mui/material'
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_THERAPY_SESSION,
  MOMENT_FORMATS,
} from 'src/consumer/constants'
import { isValidNumber } from 'src/consumer/helpers/utilFunctions'
import { useHasScrolled } from 'src/customHooks/customHooks'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'
import { CollapseIcon } from '../../clinicSchedule/components/styledComponents'
import {
  getPayableToAUMHUMStringForClinicBrand,
  getPayableToAUMHUMStringForTherapist,
} from '../util'
import {
  SummaryCard,
  SummaryHeader,
  SummaryHeaderContent,
  SummaryBody,
  SummaryGrid,
  SummaryBlock,
  SummaryBlockLabel,
  SummaryKVRow,
  SettlementCallout,
  CollectionCardWrapper,
} from './reportSummaryStyledComponents'

export default function PayoutReportSummary({
  summaryReport,
  totalSessionTitle = 'Total Sessions',
  eventType,
  allEventTypes,
  currentDate,
  previousDate,
  loadingSummary = false,
  filters,
  forMentor = false,
  apiEndPoint = 'hello',
  entityName = '',
  forTherapist = false,
  includeNonAumhumInSummary = true,
}) {
  const theme = useTheme()
  const [isUserCollapsed, setIsUserCollapsed] = useState(true)
  const summaryCurrency = summaryReport?.totalFeeCollectedCurrency
  const hasScrolled = useHasScrolled()

  let summaryTitle = forMentor ? 'Summary' : `Clinic Bookings Summary`
  let summaryTitleTimeHeading = ''
  let summaryCollectedByClinic = 'Collected By Clinic'
  let summaryCollectedByAUMHUM = 'Collected By AUMHUM'

  const isSummaryCollapsed = useMemo(() => {
    if (!hasScrolled) {
      return false
    }
    return isUserCollapsed
  }, [hasScrolled, isUserCollapsed])

  // Helper function to safely format a date
  const safeFormatDate = (date, format) => {
    if (!date) return ''
    let dateToFormat = null
    
    // Check if it's a valid dayjs object
    if (dayjs.isDayjs(date)) {
      if (date.isValid()) {
        dateToFormat = date
      }
    } else {
      // Try to parse as dayjs if it's not already a dayjs object
      const parsed = dayjs(date)
      if (parsed.isValid()) {
        dateToFormat = parsed
      }
    }
    
    // If we have a valid date, check if it's finite before formatting
    if (dateToFormat) {
      try {
        const dateObj = dateToFormat.toDate()
        if (isFinite(dateObj.getTime())) {
          return dateToFormat.format(format)
        }
      } catch (error) {
        // If toDate() fails, return empty string
        return ''
      }
    }
    return ''
  }

  if (currentDate) {
    const formatted = safeFormatDate(currentDate, MOMENT_FORMATS.completeTimeDetailsWithTimeZoneName)
    if (formatted) {
      summaryTitleTimeHeading = formatted
    }
  }
  if (previousDate && !summaryTitleTimeHeading) {
    const formatted = safeFormatDate(previousDate, MOMENT_FORMATS.completeTimeDetailsWithTimeZoneName)
    if (formatted) {
      summaryTitleTimeHeading = formatted
    }
  }

  let summaryTitleTimeShort = summaryTitleTimeHeading
  if (currentDate && !previousDate) {
    const d = dayjs.isDayjs(currentDate) && currentDate.isValid() ? currentDate : dayjs(currentDate)
    if (d.isValid()) summaryTitleTimeShort = d.format('MMM D, YYYY')
  }
  if (previousDate && !currentDate) {
    const d = dayjs.isDayjs(previousDate) && previousDate.isValid() ? previousDate : dayjs(previousDate)
    if (d.isValid()) summaryTitleTimeShort = d.format('MMM D, YYYY')
  }
  if (currentDate && previousDate) {
    let prevDate = null
    let currDate = null
    if (dayjs.isDayjs(previousDate) && previousDate.isValid()) {
      prevDate = previousDate.startOf('day')
    } else {
      const parsed = dayjs(previousDate)
      if (parsed.isValid()) prevDate = parsed.startOf('day')
    }
    if (dayjs.isDayjs(currentDate) && currentDate.isValid()) {
      currDate = currentDate.endOf('day')
    } else {
      const parsed = dayjs(currentDate)
      if (parsed.isValid()) currDate = parsed.endOf('day')
    }
    if (prevDate && currDate) {
      const prevFormatted = safeFormatDate(prevDate, MOMENT_FORMATS.completeTimeDetailsWithTimeZoneName)
      const currFormatted = safeFormatDate(currDate, MOMENT_FORMATS.completeTimeDetailsWithTimeZoneName)
      if (prevFormatted && currFormatted) {
        summaryTitleTimeHeading = `${prevFormatted} to ${currFormatted}`
      }
      // Parent passes currentDate = reportDate (start), previousDate = reportEndDate (end)
      summaryTitleTimeShort = `${currDate.format('MMM D, YYYY')} – ${prevDate.format('MMM D, YYYY')}`
    }
  }

  // summaryTitleTimeHeading += ` (${currentDate?.tz().guess()})`

  let summaryTotalEventConductedTitle = forMentor ? 'Total Sessions' : 'Total Clinic Bookings'

  const currentEventTypeObject = allEventTypes?.find((eve) => eve?.id === eventType)
  summaryTitle = `${currentEventTypeObject?.title} Summary`
  if (eventType === EVENT_TYPE_CONSULTATION) {
    summaryTotalEventConductedTitle = 'Total Consultations'
    summaryCollectedByClinic = 'Consultations (Collected By Clinic)'
    summaryCollectedByAUMHUM = 'Consultations (Collected By AUMHUM)'
  }

  if (eventType === EVENT_TYPE_THERAPY_SESSION) {
    summaryTotalEventConductedTitle = 'Total Therapies'
    summaryCollectedByClinic = 'Therapies (Collected By Clinic)'
    summaryCollectedByAUMHUM = 'Therapies (Collected By AUMHUM)'
  }

  const totalCollectedFee =
    summaryReport?.feeCollectedByAUMHUM + summaryReport?.feeCollectedByClinic

  const collectedByAumhumAumhumShare =
    summaryReport?.feeCollectedByAUMHUM - summaryReport?.portionPayableToClinic
  const collectedAtClinicClinicShare =
    summaryReport?.feeCollectedByClinic - summaryReport?.portionPayableToAUMHUM

  const clinicRevenue = collectedAtClinicClinicShare + summaryReport?.portionPayableToClinic
  const aumhumRevenue = summaryReport?.portionPayableToAUMHUM + collectedByAumhumAumhumShare

  function expandCollapseReport() {
    setIsUserCollapsed((prev) => !prev)
  }

  useEffect(() => {
    if (!hasScrolled) {
      setIsUserCollapsed(true)
    }
  }, [hasScrolled])

  return (
    <SummaryCard sx={{ position: 'relative' }}>
      {loadingSummary && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1),
            zIndex: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading
          </Typography>
          <CircularProgress size={24} />
        </div>
      )}
      <SummaryHeader>
        <div style={{ width: 40 }} />
        <SummaryHeaderContent>
          {entityName && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
              {entityName}
            </Typography>
          )}
          <Typography variant="subtitle1" fontWeight={700}>
            {summaryTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
            {summaryTitleTimeShort || summaryTitleTimeHeading}
          </Typography>
          {!forTherapist && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {includeNonAumhumInSummary
                ? 'Summary includes all sessions (AUMHUM affiliated and not affiliated to AUMHUM).'
                : 'Summary shows AUMHUM-affiliated sessions only.'}
            </Typography>
          )}
        </SummaryHeaderContent>
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: 40 }}>
          {hasScrolled && (
            <IconButton size="small" onClick={expandCollapseReport} aria-label="Collapse summary">
              <CollapseIcon collapsed={isSummaryCollapsed} />
            </IconButton>
          )}
        </div>
      </SummaryHeader>
      <Collapse in={!isSummaryCollapsed}>
        <SummaryBody>
          {!forTherapist ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr minmax(220px, auto)',
                gap: theme.spacing(2),
                alignItems: 'stretch',
              }}
              className="payout-summary-grid"
            >
              <SummaryBlockCard label="Volume">
                <KVItem title={summaryTotalEventConductedTitle} type="number" value={summaryReport?.totalNumberOfSessions} />
                <KVItem title={summaryCollectedByClinic} type="number" value={summaryReport?.sessionsWhereFeeCollectedByClinic} />
                <KVItem title={summaryCollectedByAUMHUM} type="number" value={summaryReport?.sessionsWhereFeeCollectedByAUMHUM} />
              </SummaryBlockCard>
              <SummaryBlockCard label="Revenue">
                <KVItem currency={summaryCurrency} title={`Gross ${currentEventTypeObject?.title ?? 'fee'}`} type="number" value={summaryReport?.totalFeeCollected} />
                <KVItem currency={summaryCurrency} title="Clinic share" type="number" value={clinicRevenue} highlight />
                <KVItem currency={summaryCurrency} title="AUMHUM share" type="number" value={aumhumRevenue} highlight />
              </SummaryBlockCard>
              <SummaryBlockCard label="Collected at clinic">
                <KVItem currency={summaryCurrency} title="Total collected" type="number" value={summaryReport?.feeCollectedByClinic} large />
                <KVItem currency={summaryCurrency} title="Clinic share" type="number" value={collectedAtClinicClinicShare} />
                <KVItem currency={summaryCurrency} title="AUMHUM share" type="number" value={summaryReport?.portionPayableToAUMHUM} />
              </SummaryBlockCard>
              <SummaryBlockCard label="Collected at AUMHUM">
                <KVItem currency={summaryCurrency} title="Total collected" type="number" value={summaryReport?.feeCollectedByAUMHUM} large />
                <KVItem currency={summaryCurrency} title="Clinic share" type="number" value={summaryReport?.portionPayableToClinic} />
                <KVItem currency={summaryCurrency} title="AUMHUM share" type="number" value={collectedByAumhumAumhumShare} />
              </SummaryBlockCard>
              <CollectionCardWrapper>
                <Typography className="collection-card-label">Settlement</Typography>
                <SettlementCallout>
                  <AccountBalanceRoundedIcon sx={{ fontSize: 28, color: 'primary.main', opacity: 0.9 }} />
                  <Typography className="settlement-caption">
                    {forTherapist
                      ? getPayableToAUMHUMStringForTherapist(summaryReport, filters)
                      : getPayableToAUMHUMStringForClinicBrand(summaryReport, filters)}
                  </Typography>
                  <Typography className="settlement-amount">
                    {getPriceStringWithCurrency(
                      Math.abs(Number(summaryReport?.overallPayableToAUMHUM) || 0),
                      summaryCurrency,
                    )}
                  </Typography>
                </SettlementCallout>
              </CollectionCardWrapper>
            </div>
          ) : (
            <SummaryGrid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <SummaryBlockCard label="Sessions">
                  <KVItem title={summaryTotalEventConductedTitle} type="number" value={summaryReport?.totalNumberOfSessions} />
                </SummaryBlockCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SummaryBlockCard label="Revenue">
                  <KVItem currency={summaryCurrency} title="Gross fee" type="number" value={summaryReport?.totalFeeCollected} forceCurrency />
                </SummaryBlockCard>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SummaryBlockCard label="Settlement">
                  <KVItem currency={summaryCurrency} title="Collect from AUMHUM" type="number" value={summaryReport?.overallPayableToMentor} forceCurrency large primary />
                </SummaryBlockCard>
              </Grid>
            </SummaryGrid>
          )}
        </SummaryBody>
      </Collapse>
    </SummaryCard>
  )
}

function SummaryBlockCard({ label, children }) {
  return (
    <SummaryBlock>
      <SummaryBlockLabel>{label}</SummaryBlockLabel>
      {children}
    </SummaryBlock>
  )
}

function KVItem({
  title,
  value,
  currency,
  type,
  highlight = false,
  large = false,
  primary = false,
  forceCurrency = false,
}) {
  let val = value
  if (type === 'number') {
    if (!isValidNumber(value)) {
      val = '—'
    } else {
      if (currency || forceCurrency) {
        val = getPriceStringWithCurrency(value, currency)
      } else {
        val = value
      }
    }
  }

  const valueClassNames = [
    'summary-value',
    large && 'summary-value--large',
    primary && 'summary-value--primary',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <SummaryKVRow className={highlight ? 'summary-kv-highlight' : ''}>
      <Typography component="span" className="summary-label">
        {title}
      </Typography>
      <Typography component="span" className={valueClassNames}>
        {val}
      </Typography>
    </SummaryKVRow>
  )
}

// {
//     "reportStartTime": "2025-01-26 18:30:00",
//     "reportEndTime": "2025-01-27 18:30:00",
//     "totalNumberOfSessions": 5.0,
//     "totalFeeCollectedCurrency": "inr",
//     "totalFeeCollected": 0.0,
//     "feeCollectedByClinic": 0.0,
//     "portionPayableToAUMHUM": 1750.0,
//     "feeCollectedByAUMHUM": 0.0,
//     "portionPayableToClinic": 0.0,
//     "overallPayableToAUMHUM": 1750.0
// }

// {!!summaryReport?.unableToCalculateForSessions &&
//   summaryReport?.unableToCalculateForSessions?.length > 0 && (
//     <div style={{ paddingRight: '8px', flexShrink: 0 }}>
//       <Tooltip
//         title={`Could not calculate fee shared data in ${
//           summaryReport?.unableToCalculateForSessions?.length || 0
//         } Sessions`}
//       >
//         <Badge
//           badgeContent={summaryReport?.unableToCalculateForSessions?.length || 0}
//           color="info"
//         >
//           <Error color="error" />
//         </Badge>
//       </Tooltip>{' '}
//     </div>
//   )}
